import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import CheckIcon from "@mui/icons-material/Check";

import Button from "components/common/Button";
import type { ComponentType } from "react";
import PhoneInputJs from "components/common/PhoneInput";
import { formatUsPhone, US_ONLY } from "plg/utils/usPhone";
import PageLoader from "components/common/PageLoader";
import ToolPicker from "plg/screens/Signup/ToolPicker";
import TileGrid from "components/Screens/Marketplace/Customer/Shortlist/TileGrid";
import usePlgSignupConfig from "plg/screens/Signup/usePlgSignupConfig";
import { useShortlist } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";
import { withAnswer } from "components/Screens/Marketplace/Customer/Shortlist/preferenceAskStore";
import { configTiles, preferenceSubtitle } from "components/Screens/Marketplace/Customer/utils/preferenceTiles";
import { allConfigTools, resolveOptions, suggestedTools } from "plg/utils/configSchema";
import { fetchCompanyProfile, updateCompanyProfile } from "plg/api/plgCompany";
import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";
import type { CompanyProfile } from "plg/types";
import type { ToolOption } from "components/Screens/Marketplace/Customer/types";
import { cleanTools, toolKey, toolLogos } from "components/Screens/Marketplace/Customer/utils/tools";
import "plg/plg.scss";
import "components/Screens/Marketplace/Customer/Shortlist/Shortlist.scss";
import "plg/screens/Signup/PlgSignup.scss";
import "plg/screens/Company/BusinessProfilePage.scss";
import { URGENCY_SETUP } from "plg/meeting/meetingOptions";

// Untyped JSX input; its inferred props make every prop required.
const PhoneInput = PhoneInputJs as unknown as ComponentType<Record<string, unknown>>;

interface Snapshot {
  phone: string;
  hiringTimeline: string;
  tools: string[];
  picked: string[];
}

// Order does not matter, and tools compare like everywhere else (case, spaces).
const sameSet = (a: string[], b: string[]) => {
  const ka = [...new Set(a.map(toolKey))].sort();
  const kb = [...new Set(b.map(toolKey))].sort();
  return ka.length === kb.length && ka.every((k, i) => k === kb[i]);
};
const sameSnapshot = (a: Snapshot, b: Snapshot) =>
  a.phone.trim() === b.phone.trim() &&
  a.hiringTimeline === b.hiringTimeline &&
  sameSet(a.tools, b.tools) &&
  sameSet(a.picked, b.picked);

function BusinessProfilePage() {
  const { config } = usePlgSignupConfig();
  const api = useTalentApi();
  const { items, askState, updateAskState } = useShortlist();
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [phone, setPhone] = useState("");
  const [hiringTimeline, setHiringTimeline] = useState("");
  const [tools, setTools] = useState<string[]>([]);
  const [catalogue, setCatalogue] = useState<ToolOption[]>([]);
  const [picked, setPicked] = useState<string[]>(() => askState.stated.map((t) => t.label));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  // The values as loaded or last saved: Save changes is enabled only when something differs.
  const [baseline, setBaseline] = useState<Snapshot | null>(null);

  useEffect(() => {
    fetchCompanyProfile()
      .then((p) => {
        setProfile(p);
        setPhone(p.phone || "");
        setHiringTimeline(p.hiringTimeline || "");
        setTools(p.tools || []);
        setBaseline({ phone: p.phone || "", hiringTimeline: p.hiringTimeline || "", tools: p.tools || [], picked });
      })
      .catch(() => setError(true));
    api.fetchTools()
      .then((list) => setCatalogue(list.filter((t) => t.active !== false)))
      .catch(() => setCatalogue([]));
  }, []);

  // One fixed tile set from config; what they told us before starts selected.
  const tiles = useMemo(() => configTiles(config), [config]);
  // One entry per tool whatever the spelling ("Delta dental" in the catalogue, "Delta Dental" in config).
  const options = useMemo(
    () => cleanTools([...(config ? allConfigTools(config) : []), ...catalogue.map((t) => t.name)]).sort((a, b) => a.localeCompare(b)),
    [catalogue, config]
  );
  const logos = useMemo(() => toolLogos(catalogue), [catalogue]);
  const stateLabel = useMemo(() => {
    if (!config || !profile?.state) return profile?.state;
    return resolveOptions({ key: "state", type: "select", options: "usStates" }, config).find((o) => o.value === profile.state)?.label || profile.state;
  }, [config, profile]);

  // "When are you looking to hire?": the setup question (signup config optionLists.hiringTimelines). Required.
  const timelineOptions = useMemo(() => {
    const fromConfig = config ? resolveOptions({ key: "hiringTimeline", type: "select", options: "hiringTimelines" }, config) : [];
    return fromConfig.length ? fromConfig : URGENCY_SETUP.map((v) => ({ value: v, label: v }));
  }, [config]);
  const timelineMissing = !hiringTimeline;
  const changed = !!baseline && !sameSnapshot(baseline, { phone, hiringTimeline, tools, picked });

  const dirty = () => setSaved(false);
  const toggle = (label: string) => {
    dirty();
    setPicked((p) => (p.includes(label) ? p.filter((x) => x !== label) : [...p, label]));
  };

  const save = async () => {
    if (timelineMissing) return;
    setSaving(true);
    setError(false);
    const res = await updateCompanyProfile({ phone: phone.trim() || undefined, tools, hiringTimeline });
    const chosen = tiles.filter((t) => picked.includes(t.label)).map(({ cat, label }) => ({ cat, label }));
    if (chosen.length) {
      updateAskState(withAnswer(askState, chosen));
      Promise.resolve(api.postPreferences({ source: "company", tiles: chosen, personIds: items.map((i) => i.talent._id) })).catch(() => {});
    }
    setSaving(false);
    if (res.ok === false) setError(true);
    else {
      setSaved(true);
      setBaseline({ phone, hiringTimeline, tools, picked });
    }
  };

  if (!profile && !error) return <PageLoader />;

  return (
    <div className="sl-page">
      <Helmet>
        <title>Edge | Company</title>
      </Helmet>
      <div className="sl-page__col">
        <div className="bp-head">
          <h1>Edit business profile</h1>
          <p>Your contact details and what you are looking for. Change them any time.</p>
        </div>

        {profile && (
          <section className="plg-card bp-card">
            <h3>Your business</h3>
            <dl className="bp-facts">
              <dt>Business name</dt>
              <dd>{profile.companyName}</dd>
              <dt>State</dt>
              <dd>{stateLabel || "—"}</dd>
              <dt>Industry</dt>
              <dd>{profile.industry || "—"}</dd>
              <dt>Size</dt>
              <dd>{profile.size || "—"}</dd>
            </dl>
          </section>
        )}

        <section className="plg-card bp-card">
          <h3>
            <label htmlFor="bp-hiring-timeline">When are you looking to hire?</label>
          </h3>
          <select
            id="bp-hiring-timeline"
            className={`bp-select${timelineMissing ? " bp-select--missing" : ""}`}
            value={hiringTimeline}
            onChange={(e) => {
              dirty();
              setHiringTimeline(e.target.value);
            }}
          >
            <option value="">Select</option>
            {timelineOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {timelineMissing && <div className="bp-hint">Choose when you are looking to hire.</div>}
        </section>

        <section className="plg-card bp-card">
          <h3>Phone number</h3>
          <PhoneInput
            name="phone"
            label=""
            value={phone}
            onChange={(v: string) => { dirty(); setPhone(v); }}
            placeholder="614 555 0142"
            defaultCountry="United States"
            countries={US_ONLY}
            formatDisplay={formatUsPhone}
          />
          <div className="bp-hint">So we can reach you when you ask for a call.</div>
        </section>

        <section className="plg-card bp-card">
          <h3>The apps you use</h3>
          <p className="bp-sub">We highlight people who already know them.</p>
          <ToolPicker
            value={tools}
            onChange={(next) => { dirty(); setTools(next); }}
            suggestions={config ? suggestedTools(config, profile?.industry) : []}
            options={options}
            logos={logos}
          />
        </section>

        {tiles.length > 0 && (
          <section className="plg-card bp-card">
            <h3>What you are looking for in talent</h3>
            <p className="bp-sub">{preferenceSubtitle(config, "company")}</p>
            <TileGrid tiles={tiles} selected={picked} onToggle={toggle} variant="rich" columns={4} />
          </section>
        )}

        <div className="bp-actions">
          {saved && (
            <span className="bp-saved">
              <CheckIcon fontSize="small" />
              Changes saved
            </span>
          )}
          {error && <span className="bp-error">Something went wrong. Please try again.</span>}
          <Button variant="contained" size="large" label="Save changes" onClick={save} isLoading={saving} isDisabled={timelineMissing || !changed} />
        </div>
      </div>
    </div>
  );
}

export default BusinessProfilePage;
